<template>
    <div class="grid">
        <div class="unit whole">
            <h1>{{ pupil?.name }}</h1>
            <RouterLink class="btn pull-right" to="/teacher/view-pupils">View all pupils</RouterLink>
            <p class="lead">Details on {{ pupil?.name }} are given below</p>
        </div>
    </div>

    <div class="grid">
        <div class="unit whole">
            <div class="panel">
                <div class="grid">
                    <div class="unit one-quarter">
                        <h2>Details</h2>
                        <h3>Username:</h3>
                        <p>{{ pupil?.username }}</p>
                        <template v-if="pupil?.last_active">
                            <h3>Last active:</h3>
                            <p>{{ formatDateTime(pupil.last_active) }}</p>
                        </template>
                        <h3>Current book:</h3>
                        <p>{{ pupil?.book ? pupil.book.title : 'None' }}</p>
                        <h3>Pupil choices:</h3>

                        <ul :style="pupilStyle" class="pupil_style">
                            <li>{{ pupil?.font_size || 16 }}pt {{ pupilTextColor }} text</li>
                            <li>{{ pupilBgColor }} background</li>
                            <li>Font {{ pupil?.font_name === 'od' ? 'two' : 'one' }}</li>
                            <li>{{ pupilLanguage }}</li>
                        </ul>
                    </div>

                    <div class="unit three-quarters">
                        <div class="pupil_summary_assigned">
                            <h2>Summary</h2>

                            <div class="grid">
                                <div class="unit three-tenths">
                                    <h3>Level</h3>
                                </div>

                                <div class="reading_age_centre unit one-quarter">
                                    <h3>Reading age</h3>
                                </div>

                                <div class="numeric_right unit three-twentieths">
                                    <h3>Assigned</h3>
                                </div>

                                <div class="numeric_right unit three-twentieths">
                                    <h3>Opened</h3>
                                </div>

                                <div class="numeric_right unit three-twentieths">
                                    <h3>Finished</h3>
                                </div>
                            </div>

                            <div v-for="level of bookSummary" :key="`l-${level.book_level}`" class="grid pupil_summary">
                                <div class="unit three-tenths">
                                    <p>
                                        <img
                                            :src="`/common/img/level-${level.book_level_title.replaceAll(' ', '-').toLowerCase()}.png`"
                                            :alt="level.book_level_title"
                                        />
                                        {{ level.book_level_title }}
                                    </p>
                                </div>

                                <div class="reading_age_centre unit one-quarter">
                                    <p>{{ level.book_reading_age }}</p>
                                </div>

                                <div class="numeric_right unit three-twentieths">
                                    <p>{{ level.assigned_count }}</p>
                                </div>

                                <div class="numeric_right unit three-twentieths">
                                    <p>{{ level.started_count }}</p>
                                </div>

                                <div class="numeric_right unit three-twentieths">
                                    <p>{{ level.finished_count }}</p>
                                </div>
                            </div>
                        </div>

                        <div class="pupil_summary_group">
                            <template v-if="pupil && pupil.groups.length > 0">
                                <h2>Groups</h2>
                                <ul class="ml-4">
                                    <li v-for="group of pupil.groups" :key="`g-${group.uid}`">
                                        <RouterLink to="/teacher/groups">{{ group.name }}</RouterLink>
                                    </li>
                                </ul>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="grid">
        <div class="unit whole">
            <div class="panel reading-log">
                <div class="grid">
                    <div class="unit one-fifth">Book</div>
                    <div class="unit one-fifth">Info</div>
                    <div class="unit one-quarter">Reading log</div>
                    <div class="unit seven-twentieths">Tasks</div>
                </div>
                <div v-for="book of books" :key="`book-${book.id}`" class="grid reading-log__row">
                    <div class="unit whole">
                        <TeacherPupilBook
                            :book="book"
                            :pupilId="pupilId"
                            :readingTestResults="readingTestResults"
                            :readingProfile="readingProfiles[pupilId]"
                            @updating="loading = true"
                            @unassigned="refresh"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
    <v-overlay :model-value="loading" class="align-center justify-center">
        <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { BookSummary, LibraryBook, ReadingTestResult } from '@ilteducation/giglets-types';

const route = useRoute('/teacher/pupil/[[pupilId]]');
definePage({ meta: { area: 'pupils-and-groups' } });
const { formatDateTime } = useDateFormat();
const store = useTeacherStore();
store.fetchPupils();
const { pupils } = storeToRefs(store);

const loading = ref(true);
const pupilId = computed(() => parseInt(route.params.pupilId + ''));
const bookSummary = ref<Array<BookSummary>>([]);
const books = ref<Array<LibraryBook>>([]);
const readingTestResults = ref<Record<number, ReadingTestResult>>({});

const bookReadingTimeStore = useBookReadingTimeStore();
const { fetchBookReadingTimes } = bookReadingTimeStore;
const { readingProfiles } = storeToRefs(bookReadingTimeStore);
watchEffect(() => {
    fetchBookReadingTimes([pupilId.value]);
});

await refresh();

const pupil = computed(() => {
    return pupils.value.find((p) => p.id === pupilId.value);
});

async function refresh() {
    loading.value = true;
    try {
        await Promise.all([
            $fetch<Array<BookSummary>>(`/api/teacher/current/pupils/${pupilId.value}/bookSummary`).then(
                (res) => (bookSummary.value = res),
            ),
            $fetch<Array<LibraryBook>>(`/api/teacher/current/pupils/${pupilId.value}/details`).then(
                (res) => (books.value = res),
            ),
        ]);
    } finally {
        loading.value = false;
    }
}

const pupilTasks = computed(() => {
    return books.value.flatMap((b) => b.pupil_tasks || []);
});

watchEffect(() => {
    const readingTaskIds = pupilTasks.value.filter((pt) => pt.task.reading_test).map((pt) => pt.id);
    $fetch<ReadingTestResult[]>(
        '/api/teacher/current/reading_test_results?pupil_tasks=' + readingTaskIds.join(','),
    ).then((res) => {
        res.forEach((rtr) => {
            readingTestResults.value[rtr.pupil_task_id] = rtr;
        });
    });
});

const pupilTextColor = computed(() => {
    if (pupil.value?.text_color === 'ffffff') {
        return 'White';
    } else if (pupil.value?.text_color === 'ffff00') {
        return 'Yellow';
    } else {
        return 'Black';
    }
});

const pupilBgColor = computed(() => {
    switch (pupil.value?.bg_color) {
        case '000000':
            return 'Black';
        case 'f2f2f2':
            return 'Ivory';
        case 'd8d8d8':
            return 'Grey';
        case 'ceecf5':
            return 'Blue';
        case 'f6cece':
            return 'Pink';
        case 'fee2a9':
            return 'Yellow';
        case 'f5ecce':
            return 'Sepia';
        default:
            return 'White';
    }
});

const pupilStyle = computed(() => {
    const style = {
        color: '#' + pupil.value?.text_color,
        backgroundColor: '#' + pupil.value?.bg_color,
        'font-family': '',
        'font-size': pupil.value?.font_size || 16 + 'px',
        'padding-left': pupil.value?.bg_color !== 'ffffff' ? '5px' : '0px',
    };
    if (pupil.value?.font_name === 'od') {
        style['font-family'] = 'open-dyslexic-bold';
    }
    return style;
});

const pupilLanguage = computed(() => {
    switch (pupil.value?.ui_language) {
        case 'cy':
            return 'Welsh';
        case 'gd':
            return 'Gaelic';
        case 'ga':
            return 'Irish';
        default:
            return 'English';
    }
});
</script>
<style scoped lang="scss">
.pupil_style {
    list-style-type: none;
    padding-top: 5px;
}
.reading-log {
    > div {
        border-top: 1px solid;

        &:first-of-type {
            border: unset;
            font-size: 1rem;
            font-weight: 700;
        }
    }
    &__row {
        padding: 8px 0;
    }
}
</style>
